import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import {
  directionAbbreviations,
  orientationToRotation,
  rotationToOrientation
} from 'data/constants';
import { ExposureDirectionType } from 'data/types';
import { useUpdateStructureOrientationMutation } from 'features/api/project-api';
import { setStructureOrientation } from 'features/structure/structure-thunks';
import { useCallback, useEffect, useRef } from 'react';
import { refetchStructureSpaces } from 'utils/data';

const ORIENTATION_UPDATE_DEBOUNCE_MS = 500;

type CompassProps = {
  onRotate?: (rotation: number) => void;
  enlarged?: boolean;
};

export default function Compass({ onRotate, enlarged }: CompassProps) {
  const dispatch = useAppDispatch();
  const { projectSettings } = useAppSelector((state) => state.project);
  const { structureUniqueId } = useAppSelector((state) => state.structure);
  const [updateStructureOrientation] = useUpdateStructureOrientationMutation();

  const orientationSetting = projectSettings.find(
    (s) => s.code === 'structure_orientation'
  );

  const savedOrientation = orientationSetting?.value as
    | ExposureDirectionType
    | undefined;

  const rotation =
    savedOrientation && orientationToRotation[savedOrientation] !== undefined
      ? orientationToRotation[savedOrientation]
      : 0;

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingOriginalOrientationRef = useRef<string | null>(null);
  const pendingNewOrientationRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const updateWallOrientations = useCallback(
    async (oldOrientation: string, newOrientation: string) => {
      if (!structureUniqueId) return;

      try {
        const result = await updateStructureOrientation({
          structureUniqueId,
          oldOrientation,
          newOrientation
        }).unwrap();

        if (result.affectedSpaceUniqueIds.length > 0) {
          await refetchStructureSpaces(
            result.affectedSpaceUniqueIds,
            structureUniqueId
          );
        }
      } catch (error) {
        console.error('Failed to update wall orientations:', error);
      }
    },
    [structureUniqueId, updateStructureOrientation]
  );

  const handleRotate = useCallback(
    (clockwise: boolean) => {
      const newRotation = clockwise
        ? (rotation + 45) % 360
        : (rotation - 45 + 360) % 360;
      const oldOrientation = rotationToOrientation[rotation];
      const newOrientation = rotationToOrientation[newRotation];

      if (orientationSetting) {
        dispatch(
          setStructureOrientation({
            oldOrientation,
            newOrientation
          })
        );

        if (pendingOriginalOrientationRef.current === null) {
          pendingOriginalOrientationRef.current = oldOrientation;
        }

        pendingNewOrientationRef.current = newOrientation;

        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
          const originalOrientation = pendingOriginalOrientationRef.current;
          const finalOrientation = pendingNewOrientationRef.current;
          pendingOriginalOrientationRef.current = null;
          pendingNewOrientationRef.current = null;

          if (originalOrientation && finalOrientation) {
            updateWallOrientations(originalOrientation, finalOrientation);
          }
        }, ORIENTATION_UPDATE_DEBOUNCE_MS);
      }

      onRotate?.(newRotation);
    },
    [dispatch, onRotate, orientationSetting, rotation, updateWallOrientations]
  );

  const handleClick = () => {
    handleRotate(true);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleRotate(false);
  };

  const getDirection = () => {
    const orientation = rotationToOrientation[rotation];
    return orientation ? directionAbbreviations[orientation] : 'N';
  };

  return (
    <button
      type='button'
      id='compass'
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className={clsx(
        'relative flex items-center justify-center rounded-lg bg-white shadow-md',
        'transition-all hover:bg-gray-50 hover:shadow-lg',
        'focus:outline-none focus:ring-2 focus:ring-blue-500',
        enlarged ? 'p-3' : 'p-2'
      )}
    >
      <div className={clsx('relative', enlarged ? 'h-16 w-16' : 'h-10 w-10')}>
        <div
          className='absolute inset-0 flex items-center justify-center rounded-full border-2 border-gray-300 bg-white'
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          <div
            className={clsx(
              'absolute flex items-center justify-center font-bold text-red-600',
              enlarged
                ? '-top-2.5 h-5 w-5 text-xs'
                : '-top-2 h-4 w-4 text-[10px]'
            )}
            style={{ transform: `rotate(${-rotation}deg)` }}
          >
            N
          </div>
          <div
            className={clsx(
              'absolute flex items-center justify-center font-medium text-gray-600',
              enlarged
                ? '-right-2.5 h-5 w-5 text-xs'
                : '-right-2 h-4 w-4 text-[10px]'
            )}
            style={{ transform: `rotate(${-rotation}deg)` }}
          >
            E
          </div>
          <div
            className={clsx(
              'absolute flex items-center justify-center font-medium text-gray-600',
              enlarged
                ? '-bottom-2.5 h-5 w-5 text-xs'
                : '-bottom-2 h-4 w-4 text-[10px]'
            )}
            style={{ transform: `rotate(${-rotation}deg)` }}
          >
            S
          </div>
          <div
            className={clsx(
              'absolute flex items-center justify-center font-medium text-gray-600',
              enlarged
                ? '-left-2.5 h-5 w-5 text-xs'
                : '-left-2 h-4 w-4 text-[10px]'
            )}
            style={{ transform: `rotate(${-rotation}deg)` }}
          >
            W
          </div>
          <div
            className={clsx(
              'relative rounded-full bg-gray-400',
              enlarged ? 'h-2 w-2' : 'h-1.5 w-1.5'
            )}
          >
            <div
              className={clsx(
                'absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 border-transparent border-b-gray-400',
                enlarged
                  ? 'translate-y-[2px] border-x-[4px] border-b-[14px]'
                  : 'translate-y-[2px] border-x-[3px] border-b-[10px]'
              )}
            />
          </div>
        </div>
      </div>
      <Tooltip
        text={`Left-click: rotate clockwise | Right-click: rotate counter-clockwise (Current: ${getDirection()})`}
      />
    </button>
  );
}
